import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { sanitize } from 'dompurify';
import { Undo2 } from 'lucide-react';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import { setClientIdLocalStorage } from '../../../utils/client-navigation';
import { CLIENT_V1_PATH, setClientPageVersion } from '../../../utils/client-route';
import { getAgeFromDob } from '../../../utils/date';
import ImportContactsDialog from '../../../components/ImportContactsDialog/ImportContactsDialog';
import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary';
import { SlideTransition } from '../../../components/Transition/Transition';
import EditFamilyDialog from '../../../components/EditFamilyDialog/EditFamilyDialog';
import ClientEntryDialogs from '../../../components/ClientEntry/ClientEntryDialogs';
// The v46 C.P.I. explainer. It keeps every string of V1's cpiDetailPopup (by
// message id) and adds the one thing that popup never said: which band THIS
// adviser is in. V1's own copy stays where it is, still used by the V1 page.
import ClientBasePopup from './partials/ClientBasePopup';
import GPTProductRecommendation from '../../../components/ClientEntry/ChatGPTPresetDrawer/GPTProductRecommendation/GPTProductRecommendation';
import { GPTProductRecommendationButton } from '../../../components/ClientEntry/ChatGPTPresetDrawer/GPTProductRecommendation/GPTProductRecommendationButton';
import GeneralVoiceWithAiButtonUntyped from '../../../components/generalVoiceWithAiButton/generalVoiceWithAiButton';
import NotificationCenterModal from '../../../components/NotificationCenterModal/NotificationCenterModal';
import NotificationBellButton from '../../../components/NotificationBellButton/NotificationBellButton';
import TopNavHelpButton from '../../../components/TopNavHelpButton/TopNavHelpButton';
import { isSubscriptionAllowAction } from '../../../utils/user-subscription-store';
import { getRandomTargetClient } from '../../../utils/client';
import { ga4TrackSupport, tracking } from '../../../utils/tracking';
import ClientV2 from './component';
import { FilterVoiceButton } from './partials/SmartFilterSidebar';
import { parseVoiceClientFilter } from './clientSearch';
import {
  legacyClientStateInputFromLocation,
  legacyClientStateInputOf,
  movePolicyTargetErrorOf,
  msgOptOf
} from './clientLegacyState';
import type { ClientFilters } from './clientFilters';
import type { ClientLegacyStateInput, ClientLegacyStateRequest } from './clientLegacyState';
import {
  actionableClientOf,
  createClientActionHandler,
  openBirthdayDialog,
  openConnectionDialog,
  openPersonalityDialog
} from './clientActions';
import { canClientAction, clientRestrictionReasonOf } from './clientEntitlements';
import type { ClientCapability } from './clientEntitlements';
import { readRecruitMode } from './clientViewState';
import { dropClientNotesRootEdits } from './clientNotesStore';
import { useNotesSaveFailedToast } from './useNotesSaveFailedToast';
import type { ClientV2Props } from './component';
import type { QuickFactKey } from './partials/ClientDetailDrawer';
import type { HistoryLike } from './clientActions';
import type { ClientTierChoice, ExistingClientChoice, IntlLike } from './clientPresentation';
import type { QueueClient } from '../../../types/client';

/**
 * How long the move-policy exit waits for `history.go(-2)` to land before
 * navigating anyway. Only reached when there is nothing to pop.
 */
const MOVE_POLICY_POP_TIMEOUT_MS = 400;

/**
 * Where `TopNavigationPortal` renders (topNavigation.js:827-829). Inlined
 * rather than imported: `components/topNavigation` pulls `store/auth/duck` ->
 * `@capawesome/capacitor-app-review`, whose ESM breaks jest's CJS transform and
 * takes this file's whole test suite down (Established Pattern #1). The portal
 * itself is one MUI `<Portal>` with this selector, so the import buys nothing.
 */
const TOP_NAV_CONTAINER = () => document.querySelector('.top-navigation > div');

const ProductRecommendationButton: any = GPTProductRecommendationButton;
const ClientV2HelpButton: any = TopNavHelpButton;
const ImportContactsModal: any = ImportContactsDialog;
// Legacy .js component behind injectIntl, so it arrives with no props type at
// all. Cast here, the same way as the three above, rather than widening
// injectIntl's return in vendor.d.ts — that widening silenced genuine
// prop-name mistakes in every .tsx file (it was hiding two of them).
const GeneralVoiceWithAiButton: any = GeneralVoiceWithAiButtonUntyped;
// Loaded only for advisers who can use Team Bot. A static import pulls the
// OpenAI/fetch stack (and Capacitor AppReview) into every ClientV2 visit and its
// Jest boundary even when the grey card cannot be opened. The .js default is
// `withRouter(injectIntl(connect(...)))`; official types then strip non-route
// props, so cast like the other untyped JS wrappers above.
const RecruitmentTeamBotDialog: any = React.lazy(() => import('../../../components/ChatBot/ChatBotDialog'));

type ProductRecommendationRef = {
  handleCheckAndSend?: () => void;
};

export type ClientV2BridgeProps = ClientV2Props & {
  intl: IntlLike & { locale?: string };
  history?: HistoryLike;
  location?: Record<string, unknown>;
  userDetails?: { name?: string; personality?: string | null };
  toggleDialog: (dialog: string, content: Record<string, unknown>) => unknown;
  mergeDialog: (dialog: string, content: Record<string, unknown>) => unknown;
  toggleControl: (key: string, content: Record<string, unknown>) => unknown;
  updateFactSheet: (payload: Record<string, unknown>) => unknown;
  /**
   * `client.updateFactSheetPhase` — LOADING while a write is in flight, then
   * `true` on success, and either ERROR or `false` when it did not land (the
   * duck stores `payload.success`, so a 200 carrying `success: false` is a
   * failure too).
   *
   * NOT watched for failures any more, and it cannot be: it is a single global
   * shared by every factsheet write, so when two overlap it only ever describes
   * whichever settled last. The two counters below are what this page reads.
   * Kept on the props because the rest of the app still passes it around.
   */
  updateFactSheetPhase?: unknown;
  /**
   * How many factsheet writes are still in the air (`client.pendingFactWrites`).
   * The drawer holds its optimistic echo while this is above zero. `phase ===
   * LOADING` used to stand in for it and dropped to false on the FIRST write to
   * come back, retracting an echo whose own write was still going.
   */
  pendingFactWrites?: number;
  /**
   * `client.factWriteFailureVersion` — bumped once per write that did not land.
   * Watched below; an increase, not a transition, is the failure signal. See
   * `factWriteFailed`.
   */
  factWriteFailureVersion?: number;
  updateViewingClient: (client: QueueClient) => unknown;
  enableTracking: (payload: Record<string, unknown>) => unknown;
  getClientDetails: (payload: Record<string, unknown>) => unknown;
  resetAnalysisDefaultValues: () => unknown;
  initPhase: () => unknown;
  clearLastAddedClient?: () => unknown;
  getUser: () => unknown;
  getNotificationCenterList?: () => unknown;
  // Not part of ClientV2Props: the trigger for these now lives entirely in
  // the top-nav portal below, so the page and its leaves never see them.
  notificationCount?: number;
  hasNotifications?: boolean;
  clientsToSendNum?: number;
  clearAllMsgOpt?: () => unknown;
  /** Set by CLEAR_ALL_MSGOPT_SUCCESS; the page refetches clients when it asked. */
  clearAllThenFetchClient?: boolean;
  /** Optimistically merges changed factsheet fields back into clientData. */
  updateClientData?: (payload: Record<string, unknown>) => unknown;
  movePolicy?: (data: { clientId: string; portfolioId: string }, originalClientId?: string) => unknown;
  /** 'processing' | 'success' | 'error' from the policy duck's movePolicy cycle. */
  movePolicyStatus?: string;
  /** Existing recruitment tools. The drawer always renders their cards and
   * uses these booleans only to decide whether each card is active or grey. */
  teamHubAvailable?: boolean;
  teamBotAvailable?: boolean;
  aiCoachAvailable?: boolean;
  /**
   * A.I. 智能助理 (FEATURE_GPT or Ultra). Without it the A.I. cards stay on screen,
   * greyed, and a tap offers the upgrade instead of opening /gpt-options — which
   * would send the adviser to /home.
   */
  aiAvailable?: boolean;
  /**
   * Whether this adviser holds the 招募模式 campaign code — see
   * `shouldEnableRecruitmentMode` in utils/user.js, where the container
   * resolves it. Passed straight through; the bridge has no opinion on it.
   */
  recruitmentMode?: boolean;
};

/**
 * Production-only integration boundary. The page and its leaf components stay
 * easy to test; legacy dialogs and routing actions are mounted here, without
 * importing ClientEntry.js or ClientEntry/utils.js into the ClientV2 leaves.
 */
const ClientV2Bridge = (props: ClientV2BridgeProps) => {
  const [familyClient, setFamilyClient] = useState<QueueClient | null>(null);
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [savedFamilies, setSavedFamilies] = useState<string[]>([]);
  const [productRequest, setProductRequest] = useState<{ client: QueueClient; token: number } | null>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [manageFamiliesOpen, setManageFamiliesOpen] = useState(false);
  const [clearAllModalOpen, setClearAllModalOpen] = useState(false);
  const [voiceFilterRequest, setVoiceFilterRequest] = useState<{
    token: number;
    searchTerm: string;
    filters: ClientFilters;
  } | null>(null);
  const [focusClientRequest, setFocusClientRequest] = useState<{ token: number; clientId: string } | null>(null);
  const [legacyStateRequest, setLegacyStateRequest] = useState<ClientLegacyStateRequest | null>(null);
  // "Show me this date on the calendar", pushed by the meeting-note dialog.
  const [calendarRequest, setCalendarRequest] = useState<{ token: number; day: string } | null>(null);
  const calendarSequenceRef = useRef(0);
  const [familyFilterRequest, setFamilyFilterRequest] = useState<{
    token: number;
    oldFamilyName: string;
    newFamilyName?: string;
  } | null>(null);
  const [movePolicyState, setMovePolicyState] = useState<{
    portfolioToMove: any;
    originalClientId?: string;
  } | null>(null);
  const [movePolicyReminderOpen, setMovePolicyReminderOpen] = useState(false);
  const [movePolicyConfirmClient, setMovePolicyConfirmClient] = useState<QueueClient | null>(null);
  const [movePolicyPending, setMovePolicyPending] = useState(false);
  const [clientBaseOpen, setClientBaseOpen] = useState(false);
  // The band the indicator was showing when it was tapped. Deliberately NOT
  // re-derived here from `clientData`: the page keeps the last known band across
  // a refresh whose payload carries none (the server omits clientBaseLevel below
  // six valid-C.P.I. clients, and only ever puts it on clients[0]), so a second
  // read would open the explainer on 資料不足 under an indicator still reading 高.
  const [clientBaseLevel, setClientBaseLevel] = useState<number | null>(null);
  const [importChooserOpen, setImportChooserOpen] = useState(false);
  const [importContactsOpen, setImportContactsOpen] = useState(false);
  const [clearAllRequested, setClearAllRequested] = useState(false);

  const productRef = useRef<ProductRecommendationRef | null>(null);
  const requestSequenceRef = useRef(0);
  const triggeredProductTokenRef = useRef(0);
  const voiceSequenceRef = useRef(0);
  const focusSequenceRef = useRef(0);
  const legacyStateSequenceRef = useRef(0);
  const consumedLegacyLocationRef = useRef('');
  const familySequenceRef = useRef(0);
  const consumedMovePolicyRef = useRef('');
  const consumedGptRef = useRef('');
  const movePolicyTargetRef = useRef<QueueClient | null>(null);

  useEffect(() => {
    if (
      productRequest &&
      triggeredProductTokenRef.current !== productRequest.token &&
      productRef.current?.handleCheckAndSend
    ) {
      triggeredProductTokenRef.current = productRequest.token;
      productRef.current.handleCheckAndSend();
    }
  }, [productRequest]);

  const emitLegacyStateRequest = (input: ClientLegacyStateInput) => {
    legacyStateSequenceRef.current += 1;
    setLegacyStateRequest({ ...input, token: legacyStateSequenceRef.current });
    if (input.clientId) {
      focusSequenceRef.current += 1;
      setFocusClientRequest({ token: focusSequenceRef.current, clientId: input.clientId });
    }
  };

  useEffect(() => {
    // 1. Legacy client state input (notifications & deep links)
    const input = legacyClientStateInputFromLocation(props.location);
    if (input) {
      const signature = JSON.stringify([input.state, input.clientId || '', input.referenceDate || '']);
      if (signature !== consumedLegacyLocationRef.current) {
        consumedLegacyLocationRef.current = signature;
        emitLegacyStateRequest(input);
        if (input.state === 'ClientToSend') {
          setClearAllModalOpen(true);
        }
      }
    }

    // 2. Move Policy action from location state
    const locationState = props.location?.state as Record<string, any> | undefined;
    const action = locationState?.action;
    const portfolioToMove = locationState?.portfolioToMove;
    if (action === 'movePolicy' || portfolioToMove) {
      const signature = JSON.stringify([action, portfolioToMove?._id || portfolioToMove?.id || '']);
      if (consumedMovePolicyRef.current !== signature) {
        consumedMovePolicyRef.current = signature;
        setMovePolicyState({
          portfolioToMove,
          originalClientId: locationState?.originalClientId || portfolioToMove?.userId
        });
        setMovePolicyReminderOpen(true);
      }
    }

    // 3. GPT Ice-breaking or Smart Idea location triggers
    const locationSelect = props.location?.select as string | undefined;
    const gptState = locationState?.gpt as string | undefined;
    const selectedClientState = (locationState?.selectedClient || props.location?.selectedClient) as
      | QueueClient
      | undefined;
    const smartIdeaState = props.location?.smartIdea;

    if (locationSelect === 'lucky' && selectedClientState && smartIdeaState) {
      const signature = JSON.stringify(['lucky', selectedClientState._id || '']);
      if (consumedGptRef.current !== signature) {
        consumedGptRef.current = signature;
        props.toggleDialog('smartIdea', {
          client: actionableClientOf(selectedClientState, props.userEmail),
          extraHeading:
            typeof smartIdeaState === 'string' ? props.intl.formatMessage({ id: smartIdeaState }) : undefined,
          communicationTab: true
        });
      }
    } else if (gptState === 'ice-breaking' && selectedClientState) {
      const signature = JSON.stringify(['ice-breaking', selectedClientState._id || '']);
      if (consumedGptRef.current !== signature) {
        consumedGptRef.current = signature;
        props.toggleControl('chatGPTIceBreakingDrawer', {
          client: actionableClientOf(selectedClientState, props.userEmail),
          onDrawerClosed: () => {
            if (props.history) {
              props.history.replace({
                ...props.location,
                state: {
                  ...(props.location?.state as Record<string, unknown>),
                  gpt: null,
                  selectedClient: null
                }
              });
            }
          }
        });
      }
    }
    // 4. "See on calendar", from the meeting-note dialog's reminder row.
    //
    // A fresh token on every location carrying the key, rather than a dedupe on
    // the day: this dialog can be opened FROM /clientv2, and pushing /clientv2
    // onto /clientv2 remounts nothing — so asking for the same day twice in a
    // row has to take effect the second time as well.
    const calendarDay = props.location?.calendarDay as string | undefined;
    if (typeof calendarDay === 'string' && calendarDay) {
      calendarSequenceRef.current += 1;
      setCalendarRequest({ token: calendarSequenceRef.current, day: calendarDay });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location]);

  const requirePro = (payload?: Record<string, unknown>): boolean =>
    Boolean(isSubscriptionAllowAction({ type: 'core', nicknames: ['Pro', 'Ultra'] }, payload));

  // Why a locked control did nothing. A toast rather than a tooltip because
  // `title` never surfaces on touch, and the phone build is where the row's
  // greyed-out shortcuts would otherwise be silent. Deduped by react-toastify's
  // `toastId` so mashing a locked tile stacks one message, not six.
  const handleRestricted = (client: QueueClient, capability: ClientCapability) => {
    const reasonKey = clientRestrictionReasonOf(client, capability);
    if (!reasonKey) return;
    toast.info(IconToast('info', props.intl.formatMessage({ id: reasonKey })), {
      className: 'new-toast',
      toastId: `clientv2-restricted-${reasonKey}`
    });
  };

  /**
   * A factsheet write that did not land.
   *
   * The drawer answers 補資料 and 客戶狀態 optimistically, because it has to: one
   * tap costs three round trips (the write, then the GET_CLIENT_BY_ID and
   * GET_USER its epic chains) before the store can confirm anything, and a
   * button that waits for all three looks broken. The cost of that bargain is
   * that a write which FAILS leaves the drawer showing an answer nobody saved —
   * a tick, a countdown that went down, and a 補資料 tile that may have gone
   * away — while the same field on V1's factsheet page reads blank. With both
   * pages live at once that is not a stale pixel, it is the two of them telling
   * the adviser different things about their own data, and the new one lying.
   *
   * So the echo is only allowed to outlive the write while the write might still
   * succeed. The token below says "the last one didn't"; the drawer drops every
   * echo it is holding and falls back to the store, which is the truth.
   *
   * Driven by `factWriteFailureVersion` and not by the phase. This used to watch
   * the phase for a `LOADING -> ERROR|false` transition, which is only a
   * complete account of the writes when they happen one at a time — and the
   * 補資料 popup exists precisely so they DON'T. Two answers a few hundred
   * milliseconds apart overlap in a `mergeMap` epic, and if the first to return
   * SUCCEEDS the phase reads LOADING -> true -> ERROR: the watched edge never
   * occurs, the failure is dropped on the floor, and the adviser is told nothing
   * at all. Measured against the running app on 2026-08-27 with the second
   * write forced to fail: no toast, ever. Sequencing the same two taps 2.5s
   * apart produced the toast, which is what makes this a race and not a
   * dead code path.
   *
   * A count fixes that because it survives being overtaken: every failure bumps
   * it exactly once, whatever its siblings did. Only an INCREASE counts, and the
   * first run is skipped, for the same reason the LOADING edge was there before
   * — arriving on this page while the store still holds a failure from
   * somewhere else must not announce something the adviser did not just do.
   *
   * Still every echo rather than the one field that failed: the counter says a
   * write failed, not which. Attributing it needs a per-request id threaded
   * through the epic, which is a larger change than this one and buys precision
   * on top of correctness rather than correctness itself.
   */
  const [factWriteFailed, setFactWriteFailed] = useState(0);
  const factWriteFailureRef = useRef(props.factWriteFailureVersion);

  useEffect(() => {
    const previous = factWriteFailureRef.current;
    const version = props.factWriteFailureVersion;
    factWriteFailureRef.current = version;
    if (previous === undefined || version === undefined) return;
    if (version <= previous) return;
    setFactWriteFailed(token => token + 1);
    // 他是誰's 現職 / 介紹人 are factsheet writes too: drop what did not land.
    dropClientNotesRootEdits();
    toast.error(IconToast('error', props.intl.formatMessage({ id: 'clientv2-fact-save-failed' })), {
      className: 'new-toast',
      toastId: 'clientv2-fact-save-failed'
    });
  }, [props.factWriteFailureVersion, props.intl]);

  // A failed adviser-notes write (clientNotesStore) says so the same way a failed
  // fact write does — same message, same toastId, so the two never stack.
  useNotesSaveFailedToast(props.intl);

  /**
   * 生日解鎖 pays out 智能 IDEA the instant a first dob is answered, on a date
   * that has been written but not yet confirmed — the dialog is handed the new
   * value directly because it reads the client it was GIVEN and not the store
   * (SmartIdeaDialog.js). That is the right trade while the write is going: the
   * reward is the whole point, and waiting a round trip for it reads as a
   * misfire.
   *
   * It stops being the right trade the moment the write comes back failed. The
   * drawer retracts the date on that signal, but the dialog in front of it does
   * not — it keeps deriving a star sign, a birthday number and a soul number
   * from a date the record never took, over a panel that has already gone back
   * to blank and a toast that says the answer did not save. So the payout is
   * taken back with everything else.
   *
   * Held as the failure count seen when the unlock OPENED, rather than a bare
   * flag, so this only ever answers for failures that arrived after it: a dialog
   * still open an hour later is not closed by an unrelated write going wrong.
   * Cleared once every write has settled, which is the point the dob is known to
   * have landed.
   *
   * Read off the STORE's `factWriteFailureVersion` and not the local
   * `factWriteFailed` token, which is a render behind it — the token is set by
   * an effect, so on the commit that carries the failure it still reads its old
   * value, and the "everything settled" branch below would clear the unlock one
   * render before the failure branch could act on it.
   *
   * A failure during a burst that ALSO contained the dob closes the dialog even
   * if the dob itself was the write that succeeded. That is the same bargain the
   * echo retraction above already makes — one global signal cannot say which
   * write it belonged to — and it errs the safe way: nothing on screen outlives
   * a failure it might have been part of.
   */
  const birthdayUnlockRef = useRef<number | null>(null);
  const handleBirthdayUnlock = () => {
    // Armed only while a write is actually in the air, which is the entire
    // window this guard exists for — an unconfirmed dob. Once the write has
    // settled there is nothing left to take back: a dob that landed is the
    // record, and a dob that failed took the offer button down with it
    // (ClientDetailDrawer's echo retraction), so this cannot even be reached.
    //
    // The check is what keeps the guard from outliving its errand. 生日解鎖 is
    // a SEPARATE tap now rather than something `commitFact` springs, so by the
    // time the adviser presses it the write has usually already come back and
    // `pendingFactWrites` is 0. Arming on that reading left a ref that nothing
    // could clear — the effect below only re-runs when one of its two counters
    // moves, and with the burst finished neither ever would — so the guard sat
    // there for the rest of the page's life and closed 智能 IDEA on the next
    // unrelated failure, an hour and three clients later. Exactly what the
    // count was introduced to prevent.
    if ((props.pendingFactWrites ?? 0) === 0) {
      birthdayUnlockRef.current = null;
      return;
    }
    birthdayUnlockRef.current = props.factWriteFailureVersion ?? 0;
  };

  useEffect(() => {
    const openedAt = birthdayUnlockRef.current;
    if (openedAt === null) return;
    // Ordered failure-first on purpose: a failing write bumps the count and
    // drops the pending total in the SAME action, so both branches are live in
    // the same commit and the retraction has to win.
    if ((props.factWriteFailureVersion ?? 0) > openedAt) {
      birthdayUnlockRef.current = null;
      props.mergeDialog('smartIdea', { closed: true });
      return;
    }
    if ((props.pendingFactWrites ?? 0) === 0) birthdayUnlockRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.factWriteFailureVersion, props.pendingFactWrites]);

  const handleProductRecommendation = (client: QueueClient) => {
    requestSequenceRef.current += 1;
    setProductRequest({ client, token: requestSequenceRef.current });
  };

  const handleDrawerAction = createClientActionHandler({
    adviserEmail: props.userEmail,
    history: props.history,
    location: props.location,
    intl: props.intl,
    userDetails: props.userDetails,
    toggleDialog: props.toggleDialog,
    mergeDialog: props.mergeDialog,
    updateFactSheet: props.updateFactSheet,
    updateViewingClient: props.updateViewingClient,
    getClientDetails: props.getClientDetails,
    resetAnalysisDefaultValues: props.resetAnalysisDefaultValues,
    requirePro,
    // Undefined (a page that never resolved the gate) reads as available, as before.
    aiAvailable: props.aiAvailable !== false,
    requireUltra: (payload?: Record<string, unknown>) =>
      Boolean(isSubscriptionAllowAction({ type: 'core', nicknames: ['Ultra'] }, payload)),
    trackSupport: ga4TrackSupport,
    onProductRecommendation: handleProductRecommendation,
    track: tracking
  });

  // Every factsheet mutation the drawer offers goes through one of the four
  // handlers below, so they carry the same `edit-profile` gate the controls do
  // (clientEntitlements.ts) — the UI disables them, this makes it true.
  const handleEditFamily = (sourceClient: QueueClient) => {
    if (!canClientAction(sourceClient, 'edit-profile')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    if (!client.factsheetId?._id) return;
    const families = client.factsheetId?.families || [];
    setFamilyClient(client);
    setSelectedFamilies([...families]);
    setSavedFamilies([...families]);
  };

  const saveFamilies = (families: string[]) => {
    if (!familyClient) return;
    props.updateFactSheet({
      id: familyClient.factsheetId?._id,
      key: 'families',
      value: families,
      clientId: familyClient._id
    });
    setSavedFamilies([...families]);
  };

  const handleManagedFamilyRefresh = (oldFamilyName?: string, newFamilyName?: string) => {
    props.getUser();
    if (!oldFamilyName) return;

    props.getClient?.();
    familySequenceRef.current += 1;
    setFamilyFilterRequest({
      token: familySequenceRef.current,
      oldFamilyName,
      newFamilyName
    });
  };

  const handleEditTags = (sourceClient: QueueClient) => {
    if (!canClientAction(sourceClient, 'edit-profile')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    if (!client.factsheetId?._id) return;
    const savedTags = client.factsheetId.tags || [];
    props.toggleDialog('tags', {
      tags: savedTags,
      savedTags,
      onClose: (tags: typeof savedTags) => {
        if (_.isEqual(savedTags, tags)) return;
        props.updateFactSheet({
          id: client.factsheetId?._id,
          key: 'tags',
          value: tags,
          clientId: client._id,
          usedTags: {
            old_data: savedTags,
            new_data: tags
          }
        });
      }
    });
  };

  /**
   * 現有客戶, written straight to the factsheet — there is no dialog for one
   * yes/no, and the drawer's band is the control. `null` clears the field back
   * to unanswered, which the 現有客戶 filter treats as its own bucket, so it
   * must reach the server as an empty string rather than be skipped.
   */
  const handleEditExisting = (sourceClient: QueueClient, value: ExistingClientChoice | null) => {
    if (!canClientAction(sourceClient, 'edit-profile')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    if (!client.factsheetId?._id) return;
    props.updateFactSheet({
      id: client.factsheetId._id,
      key: 'existingClient',
      value: value || '',
      clientId: client._id
    });
  };

  /**
   * 補資料 answers, batched — ONE event for a sitting rather than one per tap.
   *
   * The card is answered in bursts: an adviser opens it, taps three questions
   * in a row and closes it. An event per tap would bill three times for one
   * decision and make the funnel unreadable — the question worth answering is
   * 「did they fill this client in, and how far did they get」, which is one
   * fact per sitting.
   *
   * Flushed when the DRAWER CLOSES (`onFactsSessionEnd`, wired from ClientV2 —
   * the sitting is over the moment the panel is), when the answers move to a
   * different client, and on unmount.
   *
   * The drawer-close flush is the one that makes 「a sitting」 true. Without it
   * the batch only ended on a client switch or on leaving the page, so an
   * adviser who shut the drawer and reopened the SAME client got both visits
   * merged into one event — the funnel read one sitting where there were two,
   * and a failure from the second was stamped on a batch the first had opened.
   *
   * A batch open when the app is killed is lost — accepted: these numbers are
   * read as a rate, and the alternative is an unload hook that fires
   * unreliably in a Capacitor shell anyway.
   *
   * Carries FIELD NAMES ONLY. No client id, no answer values, no dob — this is
   * a measurement of the UI, not of the client.
   */
  const factBatchRef = useRef<{
    clientId: string;
    fields: QuickFactKey[];
    firstBirthday: boolean;
    hadFailure: boolean;
  } | null>(null);

  const flushFactBatch = () => {
    const batch = factBatchRef.current;
    factBatchRef.current = null;
    if (!batch || batch.fields.length === 0) return;
    tracking('Answer Client Facts - ClientV2', {
      fields: batch.fields.join(','),
      fieldCount: batch.fields.length,
      firstBirthday: batch.firstBirthday,
      hadFailure: batch.hadFailure
    });
  };

  /**
   * Records one answer into the open batch.
   *
   * A clear is not an answer and is left out entirely: the rate this exists to
   * measure is how much of a client gets filled in, and counting the gesture
   * that empties a field would report the opposite of what happened.
   *
   * `firstBirthday` reads the STORED dob, which is still the pre-write value
   * here — the flag means 「this sitting is where the client got a birthday」,
   * the one answer that opens 智能 IDEA on the spot (ClientDetailDrawer).
   */
  const recordFactAnswer = (client: QueueClient, key: QuickFactKey, value: string | number | boolean | null) => {
    if (value === null || value === undefined || value === '') return;
    if (factBatchRef.current && factBatchRef.current.clientId !== client._id) flushFactBatch();
    const batch = factBatchRef.current || {
      clientId: client._id,
      fields: [] as QuickFactKey[],
      firstBirthday: false,
      hadFailure: false
    };
    if (!batch.fields.includes(key)) batch.fields.push(key);
    if (key === 'dob' && !client.factsheetId?.dob) batch.firstBirthday = true;
    factBatchRef.current = batch;
  };

  /**
   * A write that came back failed makes the sitting unreliable, and the event
   * has to say so — otherwise a fill rate counts answers the record never
   * took. Marked rather than dropped: the sitting still happened, and「the
   * adviser answered but it did not land」is the more useful of the two facts.
   *
   * Attribution is best-effort, and cannot be better: `updateFactSheetPhase` is
   * a single global that cannot say which write it belonged to (the same reason
   * `factWriteFailed` retracts every echo rather than one). A failure arriving
   * after its own batch has already flushed lands on the batch that is open.
   */
  useEffect(() => {
    if (factWriteFailed > 0 && factBatchRef.current) factBatchRef.current.hadFailure = true;
  }, [factWriteFailed]);

  // Leaving the page ends the sitting too — the backstop for a batch the
  // drawer-close flush never saw (the route changing out from under an open
  // drawer, rather than the drawer being closed).
  useEffect(() => () => flushFactBatch(), []);

  /**
   * 補資料 — one C.P.I. factsheet field from the drawer's popup. Same
   * adviser-owned write as 現有客戶 and 重要性.
   *
   * `dob` carries `age` with it because the server writes both from one request
   * (`clientController.updateFactSheet`: `if (key === 'dob') obj.age =
   * req.body.age`). Omitting it would store `undefined` over whatever age was
   * on file — the C.P.I. would survive, since it derives age from dob anyway,
   * but the factsheet form reads the stored field and would show it blank.
   */
  const handleEditFact = (sourceClient: QueueClient, key: QuickFactKey, value: string | number | boolean | null) => {
    if (!canClientAction(sourceClient, 'edit-profile')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    if (!client.factsheetId?._id) return;
    const payload: Record<string, unknown> = {
      id: client.factsheetId._id,
      key,
      // '' rather than null, matching the two writes above: the server assigns
      // the value straight onto the document, and the factsheet form's own
      // "never answered" state is the empty string.
      value: value === null ? '' : value,
      clientId: client._id
    };
    // `getAgeFromDob` rather than a local diff, so the age this writes is the
    // one every other reader of the pair derives — including the server's own
    // C.P.I. (server utils/date.js is the same function).
    if (key === 'dob') {
      payload.age = value ? getAgeFromDob(value, undefined) ?? '' : '';
    }
    props.updateFactSheet(payload);
    recordFactAnswer(client, key, value);
  };

  /** 重要性 — same adviser-owned factsheet field and same write as 現有客戶. */
  const handleEditTier = (sourceClient: QueueClient, value: ClientTierChoice | null) => {
    if (!canClientAction(sourceClient, 'edit-profile')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    if (!client.factsheetId?._id) return;
    props.updateFactSheet({
      id: client.factsheetId._id,
      key: 'importance',
      value: value || '',
      clientId: client._id
    });
  };

  const handleTrackingMet = (sourceClient: QueueClient, met: number) => {
    if (!canClientAction(sourceClient, 'tracking')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    const trackingId = client.trackingId?._id;
    if (!trackingId) return;
    props.enableTracking({
      met,
      trackingId,
      clientId: client._id,
      dayCount: client.trackingId?.startDate
        ? moment().diff(moment(client.trackingId.startDate, 'YYYY-MM-DD'), 'days')
        : 0
    });
  };

  // Mirrors ClientEntry.js selectOpportunity (:469-488): the same enableTracking
  // payload as 會面, plus a `dateHigh` stamp when the band is raised to H — the
  // business dashboard reads that date, so omitting it would silently break it.
  // `null` clears the band, which is what V1's control sends when the active
  // button is tapped again.
  const handleTrackingOpportunity = (sourceClient: QueueClient, opportunity: string | null) => {
    if (!canClientAction(sourceClient, 'tracking')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    const trackingId = client.trackingId?._id;
    if (!trackingId) return;
    props.enableTracking({
      opportunity,
      trackingId,
      clientId: client._id,
      dayCount: client.trackingId?.startDate
        ? moment().diff(moment(client.trackingId.startDate, 'YYYY-MM-DD'), 'days')
        : 0,
      ...(opportunity === 'H'
        ? { dateHigh: moment.utc(moment().format('YYYY-MM-DD')).format('YYYY-MM-DDT00:00:00.00Z') }
        : {})
    });
  };

  const handleAddClient = () => {
    if (!props.history) return;
    localStorage.removeItem('clientId');
    localStorage.removeItem('currentPage');
    localStorage.removeItem('clientFactsheetDataOne');
    localStorage.removeItem('clientFactsheetDataTwo');
    localStorage.removeItem('clientPersonalData');
    props.initPhase();
    // Adding someone from inside 招募模式 is adding a recruitment candidate, so
    // the form opens with 潛在招募 already on (the adviser can still turn it off).
    props.history.push(
      readRecruitMode(Boolean(props.recruitmentMode))
        ? { pathname: '/client_personal_form', state: { presetRecruitment: true } }
        : '/client_personal_form'
    );
  };

  const handlePotentialClients = () => {
    tracking('Click on Potential Clients - ClientV2');
    props.history?.push({ pathname: '/referral_records' });
  };

  // V1 has two import entries (excel -> /import, phone contacts -> native
  // contacts flow). The v46 command bar has one 匯入客戶 button, so it opens a
  // chooser that preserves both.
  const handleImportClients = () => {
    setImportChooserOpen(true);
  };

  const handleImportExcel = () => {
    tracking('Click on Import Clients - Clients');
    setImportChooserOpen(false);
    props.history?.push({ pathname: '/import' });
  };

  // Opens the native contacts picker after the permission round-trip, mirroring
  // clientPage.js importContacts. The awaits below are NOT a module-graph
  // trick: the statically imported ImportContactsDialog already imports
  // @capacitor-community/contacts, so the plugin is in the bundle and in jest's
  // graph either way. They are dynamic only so the permission round-trip and
  // the native-settings fallback are evaluated at click time.
  const importContactsFlow = async () => {
    const { Contacts } = await import('@capacitor-community/contacts');
    let permission = await Contacts.checkPermissions();
    if (permission.contacts !== 'granted') {
      permission = await Contacts.requestPermissions();
      if (permission.contacts !== 'granted') {
        const { NativeSettings, AndroidSettings, IOSSettings } = (await import('capacitor-native-settings')) as any;
        await NativeSettings.open({
          optionAndroid: AndroidSettings.ApplicationDetails,
          optionIOS: IOSSettings.App
        });
        return;
      }
    }
    setImportContactsOpen(true);
  };

  const handleImportPhoneContacts = async () => {
    tracking('Click on Import Contacts - Clients');
    setImportChooserOpen(false);
    const onClickDownloadApp = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      if (/iPhone|iPad|iPod/i.test(userAgent)) {
        window.open('https://itunes.apple.com/hk/app/portfoplus/id1590830597?mt=8', '_blank');
      } else {
        window.open('https://www.portfoplus.com/adviser/index.html', '_blank');
      }
    };

    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { Contacts } = await import('@capacitor-community/contacts');
      const permission = await Contacts.checkPermissions();
      if (Capacitor.getPlatform() === 'ios' && permission.contacts !== 'granted') {
        props.toggleDialog('permission', {
          intlId: 'contacts-full-access-required-msg',
          onClose: () => {
            void importContactsFlow();
          }
        });
      } else {
        await importContactsFlow();
      }
    } else {
      toast.info(IconToast('info', props.intl.formatMessage({ id: 'download-portfoplus-app-to-use-this-feature' })), {
        className: 'new-toast',
        onClick: onClickDownloadApp
      });
    }
  };

  // V1's aiSuggestClient (clientPage.js:215-238) does three things, not one:
  // it picks a high/medium-potential client at random, narrows the list to that
  // client (clientState 'lucky', page 0, search cleared) and expands the entry —
  // and only then opens the smart-idea dialog. The list state is what keeps the
  // suggestion on screen after the dialog is dismissed.
  const handleAiSelect = () => {
    if (!requirePro({ customMessage: props.intl.formatMessage({ id: 'upgrade-msg-filter' }) })) return;
    const targetClient = getRandomTargetClient(props.clientData);
    if (!targetClient) return;
    emitLegacyStateRequest({ state: 'lucky', clientId: targetClient._id });
    props.toggleDialog('smartIdea', {
      client: actionableClientOf(targetClient, props.userEmail),
      extraHeading: props.intl.formatMessage({ id: 'smart-idea-lucky-hint' }),
      communicationTab: true
    });
  };

  const handleVoiceFilter = (message: string) => {
    const result = parseVoiceClientFilter(message);
    if (!result) return;
    voiceSequenceRef.current += 1;
    setVoiceFilterRequest({ ...result, token: voiceSequenceRef.current });
  };

  const handleNotificationOpen = () => {
    props.getNotificationCenterList?.();
    setNotificationOpen(true);
  };

  const handleNotificationClientState = (flag: string, clientId?: string | null) => {
    const input = legacyClientStateInputOf(flag, clientId);
    if (!input) return;
    consumedLegacyLocationRef.current = JSON.stringify([input.state, input.clientId || '', '']);
    emitLegacyStateRequest(input);
  };

  const handleToggleClientToSend = (sourceClient: QueueClient) => {
    if (!canClientAction(sourceClient, 'edit-profile')) return;
    const client = actionableClientOf(sourceClient, props.userEmail);
    if (!client.factsheetId?._id) return;
    // Server field is factsheet.msgOpt (true = in the to-send list), mirroring
    // ClientEntry.js handleMsgOptChange. updateFactSheetSuccess only refreshes
    // clientDetails, so merge the new value back into clientData optimistically
    // to keep the row checkbox in sync without a full refetch.
    const next = !msgOptOf(client, props.userEmail);
    props.updateFactSheet({
      clientId: client._id,
      id: client.factsheetId._id,
      key: 'msgOpt',
      value: next,
      isInactive: Boolean(client.inactiveUser)
    });
    props.updateClientData?.({ factsheetId: { _id: client.factsheetId._id, msgOpt: next } });
  };

  // V1 refetches the client array after a page-initiated Clear All succeeds
  // (clientPage.js watches clearAllThenFetchClient gated by its own
  // clearAllPhase). The epic only calls the API; the refetch happens here.
  useEffect(() => {
    if (props.clearAllThenFetchClient && clearAllRequested) {
      setClearAllRequested(false);
      props.getClient?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.clearAllThenFetchClient, clearAllRequested]);

  // V1 drives the move-policy exit off movePolicyStatus transitions
  // (clientPage.js componentDidUpdate): toast + navigate only on success,
  // error toast + goBack on failure. Dispatch alone proves nothing.
  useEffect(() => {
    if (!movePolicyPending) return;
    if (props.movePolicyStatus === 'success') {
      setMovePolicyPending(false);
      const targetClient = movePolicyTargetRef.current;
      movePolicyTargetRef.current = null;
      setMovePolicyState(null);
      toast.info(IconToast('success', props.intl.formatMessage({ id: 'move-policy-success' })), {
        className: 'new-toast'
      });
      if (targetClient && props.history) {
        const history = props.history;
        // Mirror goToPortfolio (ClientEntry/utils.js): viewing-client dispatch,
        // clientId + factsheetDOB localStorage, DOB-derived age, and REPLACE
        // (not push).
        const goToTargetDashboard = () => {
          props.updateViewingClient(targetClient);
          setClientIdLocalStorage(targetClient._id);
          localStorage.setItem('factsheetDOB', JSON.stringify(targetClient.factsheetId?.dob ?? ''));
          history.replace({
            pathname: '/dashboard',
            state: {
              clientFactsheetDetail: {
                clientIncData: {
                  monthlyExpense: targetClient.factsheetId?.monthlyExpense,
                  monthlyIncome: targetClient.factsheetId?.monthlyIncome,
                  debt: targetClient.factsheetId?.debt,
                  age: getAgeFromDob(targetClient.factsheetId?.dob, targetClient.factsheetId?.age)
                }
              },
              client: targetClient
            }
          });
        };

        // The flow arrives here as /dashboard(source) → /insured_details → this
        // page (InsuredDetails' 移轉保單 menu item), so V1 pops two entries
        // before replacing (clientPage.js:624-625). Both are about to be wrong:
        // the policy-detail page shows a policy that has just left that client,
        // and this list is a one-shot mode. V1 fires the replace immediately
        // after `go(-2)`, which races the pop — history v4 resolves it
        // asynchronously — so wait for the pop to land instead, with a timeout
        // fallback for the case where there is nothing to pop (a deep link
        // straight into move-policy mode).
        if (history.go && history.listen) {
          let done = false;
          let unlisten: (() => void) | undefined;
          let fallbackTimer = 0;
          const finish = () => {
            if (done) return;
            done = true;
            unlisten?.();
            window.clearTimeout(fallbackTimer);
            goToTargetDashboard();
          };
          fallbackTimer = window.setTimeout(finish, MOVE_POLICY_POP_TIMEOUT_MS);
          unlisten = history.listen(finish);
          history.go(-2);
        } else {
          goToTargetDashboard();
        }
      }
    } else if (props.movePolicyStatus === 'error') {
      setMovePolicyPending(false);
      movePolicyTargetRef.current = null;
      toast.info(IconToast('error', props.intl.formatMessage({ id: 'move-policy-error' })), {
        className: 'new-toast'
      });
      props.history?.goBack?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.movePolicyStatus, movePolicyPending]);

  // The page only renders a selector on rows V1 would have rendered one on, so
  // an ineligible client cannot reach this. The guard stays as a backstop and is
  // silent — V1 has no rejection toast, it simply omits the checkbox.
  const handleSelectMovePolicyTarget = (client: QueueClient) => {
    if (!movePolicyState) return;
    const sourceId = movePolicyState.originalClientId || movePolicyState.portfolioToMove?.userId;
    if (movePolicyTargetErrorOf(client, sourceId, props.userEmail) !== null) return;
    setMovePolicyConfirmClient(client);
  };

  // V1's cancelMovePolicy is a plain history.goBack() — the whole flow exits
  // and the per-page state dies with the unmount. Mirror that for the banner.
  const handleCancelMovePolicy = () => {
    setMovePolicyState(null);
    setMovePolicyConfirmClient(null);
    props.history?.goBack?.();
  };

  const handleConfirmMovePolicy = () => {
    if (!movePolicyConfirmClient || !movePolicyState || !props.movePolicy || movePolicyPending) return;
    const portfolioId = movePolicyState.portfolioToMove._id || movePolicyState.portfolioToMove.id;
    const originalClientId = movePolicyState.originalClientId || movePolicyState.portfolioToMove.userId;

    // Dispatch only — the movePolicyStatus effect above owns the success/error
    // toast and navigation once the API actually resolves.
    movePolicyTargetRef.current = movePolicyConfirmClient;
    setMovePolicyPending(true);
    setMovePolicyConfirmClient(null);
    props.movePolicy({ clientId: movePolicyConfirmClient._id, portfolioId }, originalClientId);
  };

  const voiceLabel = props.intl.formatMessage({ id: 'clientv2-command-voice' });
  const voiceControl = (
    <span
      className="clientv2-command-slot"
      onClickCapture={event => {
        if (!requirePro({ customMessage: props.intl.formatMessage({ id: 'upgrade-msg-filter' }) })) {
          event.stopPropagation();
        }
      }}
    >
      <GeneralVoiceWithAiButton
        title="ai-voice-filter-client"
        content="ai-voice-filter-client-content"
        completeAction={handleVoiceFilter}
        specialPrompt="filter_client"
        trackDirectName="filterClientByVoice"
        trackName="AI: Filter Client By Voice"
        customButton={<FilterVoiceButton label={voiceLabel} />}
      />
    </span>
  );
  return (
    <>
      <ClientV2
        {...props}
        onConnect={client => openConnectionDialog(props.toggleDialog, client, props.userEmail)}
        onBirthday={client => openBirthdayDialog(props.toggleDialog, props.intl, client, props.userEmail)}
        onEditPersonality={client =>
          openPersonalityDialog(props.toggleDialog, props.updateFactSheet, client, props.userEmail)
        }
        canOpenFilter={() => requirePro({ customMessage: props.intl.formatMessage({ id: 'upgrade-msg-filter' }) })}
        onEditFamily={handleEditFamily}
        onEditTags={handleEditTags}
        onEditExisting={handleEditExisting}
        onEditTier={handleEditTier}
        onEditFact={handleEditFact}
        factWriteFailed={factWriteFailed}
        factWriteInFlight={(props.pendingFactWrites ?? 0) > 0}
        teamHubAvailable={Boolean(props.teamHubAvailable)}
        teamBotAvailable={Boolean(props.teamBotAvailable)}
        aiCoachAvailable={Boolean(props.aiCoachAvailable)}
        aiAvailable={props.aiAvailable !== false}
        recruitmentMode={Boolean(props.recruitmentMode)}
        onBirthdayUnlock={handleBirthdayUnlock}
        onFactsSessionEnd={flushFactBatch}
        onTrackingMet={handleTrackingMet}
        onTrackingOpportunity={handleTrackingOpportunity}
        onDrawerAction={handleDrawerAction}
        onRestricted={handleRestricted}
        onAddClient={handleAddClient}
        onSpotlightSpent={props.clearLastAddedClient}
        onPotentialClients={handlePotentialClients}
        onImportClients={handleImportClients}
        onAiSelect={handleAiSelect}
        onClientBase={level => {
          setClientBaseLevel(level);
          setClientBaseOpen(true);
        }}
        onManageFamilies={() => setManageFamiliesOpen(true)}
        voiceControl={voiceControl}
        voiceFilterRequest={voiceFilterRequest}
        focusClientRequest={focusClientRequest}
        legacyStateRequest={legacyStateRequest}
        calendarRequest={calendarRequest}
        familyFilterRequest={familyFilterRequest}
        onToggleClientToSend={handleToggleClientToSend}
        isMovePolicy={Boolean(movePolicyState)}
        movePolicySourceId={movePolicyState?.originalClientId || movePolicyState?.portfolioToMove?.userId}
        onSelectMovePolicyTarget={handleSelectMovePolicyTarget}
        onCancelMovePolicy={handleCancelMovePolicy}
      />

      {/* 通知/說明, in the top navigation bar — the same portal, same markup
          V1 uses for its own (clientPage.js:2107-2130): NotificationBellButton
          in a bare plus-btn-client.notification-btn IconButton, TopNavHelpButton
          div-wrapped in plus-btn-client.client-guideline-icon. Both classes
          carry their `right` offset from V1's own stylesheets (clientPage.scss,
          Client/styles.scss), already loaded app-wide, so no new CSS is needed
          for these two. 回到舊版 used to sit in client-guideline-icon (the only
          icon this page had); it now gets its own clientv2-backv1-icon slot
          further in, clear of both. */}
      <Portal container={TOP_NAV_CONTAINER}>
        <IconButton className="plus-btn-client notification-btn" onClick={handleNotificationOpen} size="large">
          <NotificationBellButton
            hasNotifications={props.hasNotifications}
            newNotificationNumber={props.notificationCount}
          />
        </IconButton>

        <div className="plus-btn-client client-guideline-icon">
          <div>
            <ClientV2HelpButton buttonProps={{ notClientStyle: true }} />
          </div>
        </div>

        <div className="plus-btn-client clientv2-backv1-icon">
          <IconButton
            size="small"
            data-testid="client-back-to-v1"
            aria-label={props.intl.formatMessage({ id: 'clientv2-back-to-v1' })}
            title={props.intl.formatMessage({ id: 'clientv2-back-to-v1' })}
            onClick={() => {
              // Clearing the preference sends every entry point in the app back
              // to /client, not just this navigation.
              setClientPageVersion('v1');
              props.history?.push(CLIENT_V1_PATH);
            }}
            style={{ color: '#fff' }}
          >
            <Undo2 size={20} aria-hidden="true" />
          </IconButton>
        </div>
      </Portal>

      <Dialog
        open={movePolicyReminderOpen}
        onClose={() => setMovePolicyReminderOpen(false)}
        slots={{ transition: SlideTransition }}
      >
        <DialogTitle>{props.intl.formatMessage({ id: 'move-policy-reminder-modal-title' })}</DialogTitle>
        <DialogContent>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitize(
                props.intl.formatMessage(
                  { id: 'move-policy-reminder-modal-content' },
                  {
                    policyName:
                      movePolicyState?.portfolioToMove?.policyName || movePolicyState?.portfolioToMove?.name || ''
                  }
                )
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setMovePolicyReminderOpen(false)}>
            {props.intl.formatMessage({ id: 'OK' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(movePolicyConfirmClient && movePolicyState)}
        onClose={() => setMovePolicyConfirmClient(null)}
        slots={{ transition: SlideTransition }}
      >
        <DialogTitle>{props.intl.formatMessage({ id: 'move-policy-confirm-modal-title' })}</DialogTitle>
        <DialogContent>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitize(
                props.intl.formatMessage(
                  { id: 'move-policy-confirm-modal-content' },
                  {
                    policyName:
                      movePolicyState?.portfolioToMove?.policyName || movePolicyState?.portfolioToMove?.name || '',
                    clientName:
                      movePolicyConfirmClient?.factsheetId?.name ||
                      movePolicyConfirmClient?.name ||
                      movePolicyConfirmClient?.nickname ||
                      ''
                  }
                )
              )
            }}
          />
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Button onClick={() => setMovePolicyConfirmClient(null)}>{props.intl.formatMessage({ id: 'Cancel' })}</Button>
          <Button color="primary" onClick={handleConfirmMovePolicy} disabled={movePolicyPending}>
            {props.intl.formatMessage({ id: 'Confirm' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importChooserOpen}
        onClose={() => setImportChooserOpen(false)}
        slots={{ transition: SlideTransition }}
      >
        <DialogTitle>{props.intl.formatMessage({ id: 'clientv2-command-import' })}</DialogTitle>
        <DialogContent>
          <div className="clientv2-import-chooser">
            <Button color="primary" variant="outlined" fullWidth onClick={handleImportExcel}>
              {props.intl.formatMessage({ id: 'import-clients-excel' })}
            </Button>
            <Button color="primary" variant="outlined" fullWidth onClick={() => void handleImportPhoneContacts()}>
              {props.intl.formatMessage({ id: 'import-phone-contacts' })}
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setImportChooserOpen(false)}>
            {props.intl.formatMessage({ id: 'Cancel' })}
          </Button>
        </DialogActions>
      </Dialog>

      <ClientBasePopup
        open={clientBaseOpen}
        onClose={() => setClientBaseOpen(false)}
        level={clientBaseLevel}
        intl={props.intl}
      />

      <ImportContactsModal
        open={importContactsOpen}
        onClose={() => setImportContactsOpen(false)}
        refreshClients={props.getClient}
      />

      <Dialog
        open={clearAllModalOpen}
        onClose={() => setClearAllModalOpen(false)}
        slots={{ transition: SlideTransition }}
      >
        <DialogTitle>{props.intl.formatMessage({ id: 'clear-all-title' })}</DialogTitle>
        <DialogContent>{props.intl.formatMessage({ id: 'clear-all-content' })}</DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              if (props.clientsToSendNum && props.clientsToSendNum > 0) {
                // The clearAllThenFetchClient effect refetches clients once the
                // clear succeeds; the local gate mirrors V1's clearAllPhase so a
                // stale store flag cannot trigger a refetch on its own.
                setClearAllRequested(true);
                props.clearAllMsgOpt?.();
              }
              setClearAllModalOpen(false);
            }}
            color="primary"
          >
            {props.intl.formatMessage({ id: 'clear-all' })}
          </Button>
          <Button color="primary" onClick={() => setClearAllModalOpen(false)}>
            {props.intl.formatMessage({ id: 'OK' })}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationCenterModal
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
        history={props.history}
        location={props.location}
        clientPath="/clientv2"
        handleLinkToClientState={handleNotificationClientState}
      />

      {/* No ToDo or Template drawer here: the top navigation renders its own
          TodoIconButton on this page (topNavigation.js:679-697) with the same
          two drawers behind it, so duplicating them here only produced a second
          way into the same UI. */}

      <EditFamilyDialog
        intl={props.intl}
        open={Boolean(familyClient)}
        closeDialog={() => setFamilyClient(null)}
        selectedFamilies={selectedFamilies}
        savedFamilies={savedFamilies}
        setSelectedFamilies={setSelectedFamilies}
        saveFactSheet={saveFamilies}
        refreshData={props.getClient}
        manageOnly={false}
      />

      <EditFamilyDialog
        intl={props.intl}
        open={manageFamiliesOpen}
        closeDialog={() => setManageFamiliesOpen(false)}
        refreshData={handleManagedFamilyRefresh}
        manageOnly={true}
      />

      <ProductRecommendationButton
        ref={productRef}
        toggleControl={props.toggleControl}
        client={productRequest?.client}
        language={props.intl.locale || 'en'}
      />
      <GPTProductRecommendation />
      <ClientEntryDialogs />
      {/* Boundary OUTSIDE the Suspense, because they answer different halves of
          the same load: Suspense holds the pending chunk, and only a boundary
          catches one that never arrives. The import fires on every ClientV2
          visit for an entitled adviser — this dialog is mounted whether or not
          Team Bot is ever opened — so a 404 on that chunk, which is what a
          deploy rotating the hash under a cached Capacitor index.html produces,
          used to throw past the bare Suspense to App's own boundary and replace
          the whole page.
          What is left when it does catch is NOT a grey card: the tile stays lit,
          because `teamBotAvailable` is an entitlement and knows nothing about a
          chunk, so tapping it dispatches `chatBot` and nothing opens. That is a
          worse dialog than the one that works and a far better page than the
          one that is gone, which is the whole trade — and it is where a
          `fallback` would go if the silence ever needs answering. Reporting the
          failure back to the tile would mean the boundary telling the drawer,
          and React.lazy caches the rejection anyway, so the card cannot be
          revived within the session either way. */}
      {props.teamBotAvailable && (
        <ErrorBoundary label="team-bot">
          <React.Suspense fallback={null}>
            <RecruitmentTeamBotDialog promptIdentifier="team_bot" mode="team" />
          </React.Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export default ClientV2Bridge;
